/**
 * SelectOption Builders
 *
 * Utility functions to transform entities into SelectOption arrays.
 * Provides consistent formatting and optional sorting across all forms.
 */

import type { SelectOption } from "@/components/ui/forms/Select";
import { formatEmployeeName, formatUserName } from "@/hooks/data/dataQuality";
import { formatDisplayName } from "@/utils/textUtils";
import type {
  IServiceCustomer,
  IServiceEmployee,
  IServiceProject,
  IUser,
} from "@/services/types";

/**
 * Sort configuration for SelectOption arrays.
 * - Custom function: Sort using provided comparator
 * - 'label': Sort alphabetically by label (default)
 * - false: Preserve original order
 */
export type SelectOptionSort<T> =
  | ((a: SelectOption<T>, b: SelectOption<T>) => number)
  | "label"
  | false;

/**
 * Apply sorting to SelectOption array based on sort configuration.
 */
function applySorting<T>(
  options: SelectOption<T>[],
  sort: SelectOptionSort<T>,
): SelectOption<T>[] {
  if (sort === false) {
    return options;
  }

  if (sort === "label") {
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }

  return [...options].sort(sort);
}

// ============ PROJECT OPTIONS ============

/**
 * Transform projects to SelectOption with ID as data.
 * Label format: "CODE - Name"
 *
 * @param projects - Array of projects to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toProjectOptions(
  projects: IServiceProject[],
  sort: SelectOptionSort<number> = "label",
): SelectOption<number>[] {
  const options = projects.map((p) => ({
    id: p.id,
    label: `${p.code} - ${p.name}`,
    data: p.id,
  }));

  return applySorting(options, sort);
}

/**
 * Transform projects to SelectOption with full object as data.
 * Use when custom rendering needs access to full project data.
 *
 * @param projects - Array of projects to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toProjectOptionsWithData(
  projects: IServiceProject[],
  sort: SelectOptionSort<IServiceProject> = "label",
): SelectOption<IServiceProject>[] {
  const options = projects.map((p) => ({
    id: p.id,
    label: `${p.code} - ${p.name}`,
    data: p,
  }));

  return applySorting(options, sort);
}

// ============ CUSTOMER OPTIONS ============

/**
 * Transform customers to SelectOption with ID as data.
 * Label format: Customer name (title case - converts from uppercase server data)
 *
 * @param customers - Array of customers to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toCustomerOptions(
  customers: IServiceCustomer[],
  sort: SelectOptionSort<number> = "label",
): SelectOption<number>[] {
  const options = customers.map((c) => ({
    id: c.id,
    label: formatDisplayName(c.name, "capitalize"),
    data: c.id,
  }));

  return applySorting(options, sort);
}

/**
 * Transform customers to SelectOption with full object as data.
 * Use when onChange handler needs access to full customer data.
 * Label is title case (converts from uppercase server data).
 *
 * @param customers - Array of customers to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toCustomerOptionsWithData(
  customers: IServiceCustomer[],
  sort: SelectOptionSort<IServiceCustomer> = "label",
): SelectOption<IServiceCustomer>[] {
  const options = customers.map((c) => ({
    id: c.id,
    label: formatDisplayName(c.name, "capitalize"),
    data: c,
  }));

  return applySorting(options, sort);
}

// ============ EMPLOYEE OPTIONS ============

/**
 * Transform employees to SelectOption with ID as data.
 * Label format: "FirstName LastName" (via formatEmployeeName)
 *
 * @param employees - Array of employees to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toEmployeeOptions(
  employees: IServiceEmployee[],
  sort: SelectOptionSort<number> = "label",
): SelectOption<number>[] {
  const options = employees
    .map((e) => ({
      id: e.id,
      label: formatEmployeeName(e),
      data: e.id,
    }))
    .filter((opt) => opt.label !== "");

  return applySorting(options, sort);
}

/**
 * Transform employees to SelectOption with full object as data.
 * Use when custom rendering or onChange needs access to full employee data.
 *
 * @param employees - Array of employees to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toEmployeeOptionsWithData(
  employees: IServiceEmployee[],
  sort: SelectOptionSort<IServiceEmployee> = "label",
): SelectOption<IServiceEmployee>[] {
  const options = employees
    .map((e) => ({
      id: e.id,
      label: formatEmployeeName(e),
      data: e,
    }))
    .filter((opt) => opt.label !== "");

  return applySorting(options, sort);
}

// ============ USER OPTIONS ============

/**
 * Transform users to SelectOption with full object as data.
 * Label format: "Name (email)"
 *
 * @param users - Array of users to transform
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toUserOptions(
  users: IUser[],
  sort: SelectOptionSort<IUser> = "label",
): SelectOption<IUser>[] {
  const options = users.map((user) => ({
    id: user.id,
    label: `${formatUserName(user)} (${user.email})`,
    data: user,
  }));

  return applySorting(options, sort);
}

// ============ TEAM/DEPARTMENT OPTIONS ============

/**
 * Transform team/department names to SelectOption.
 * Uses array index as ID (for string-based options).
 *
 * @param teams - Array of team names
 * @param sort - Sort configuration (default: alphabetical by label)
 */
export function toTeamOptions(
  teams: string[],
  sort: SelectOptionSort<string> = "label",
): SelectOption<string>[] {
  const options = teams.map((name, idx) => ({
    id: idx,
    label: name,
    data: name,
  }));

  return applySorting(options, sort);
}

// ============ SELECTED VALUE HELPERS ============

/**
 * Create SelectOption array from a selected project (for controlled Select value).
 * Returns empty array if no project selected.
 */
export function selectedProjectOption(
  project: IServiceProject | null | undefined,
): SelectOption<number>[] {
  if (!project) return [];
  return [
    {
      id: project.id,
      label: `${project.code} - ${project.name}`,
      data: project.id,
    },
  ];
}

/**
 * Create SelectOption array from a selected customer (for controlled Select value).
 * Returns empty array if no customer selected.
 * Label is title case (converts from uppercase server data).
 */
export function selectedCustomerOption(
  customer: IServiceCustomer | null | undefined,
): SelectOption<number>[] {
  if (!customer) return [];
  return [
    {
      id: customer.id,
      label: formatDisplayName(customer.name, "capitalize"),
      data: customer.id,
    },
  ];
}
